import {
  CATEGORY_LABELS,
  RISK_CLASS,
  RISK_LABELS,
  STANDALONE_WIZARD_KEY,
} from './accountingConstants';
import AccountingRepairScriptsPanel from '../AccountingRepairScriptsPanel';
import GasCylinderOwnersFundPanel from '../GasCylinderOwnersFundPanel';
import FleetCardOwnersFundPanel from '../FleetCardOwnersFundPanel';
export default function AccountingToolsSection({
  pumpId,
  fromDate,
  toDate,
  fixesByCategory,
  fixesIntro,
  repairScripts,
  repairScriptsIntro,
  wacAudits,
  loadingTools,
  toolsError,
  onRetryLoad,
  disabled,
  onOpenFixWizard,
  renderFixWizard,
  activeFindingKey,
  onPreviewStart,
  onPreviewEnd,
  onApplyStart,
  onApplyEnd,
  onCompleted,
  onOpenHistory,
}) {
  if (loadingTools && Object.keys(fixesByCategory || {}).length === 0) {
    return (
      <div className="admin-panel">
        <p className="small text-muted mb-0">Loading repair tools and specialist scripts…</p>
      </div>
    );
  }

  if (toolsError) {
    return (
      <div className="admin-alert-item warning">
        <strong>Could not load repair tools</strong>
        <p className="small mb-2">{toolsError}</p>
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={onRetryLoad}>Retry</button>
      </div>
    );
  }

  return (
    <>
      <div className="admin-panel accounting-intro-box mb-3">
        <h5 className="mb-1">Specialist repair tools</h5>
        <p className="small mb-0">
          Use these only when the scan guide is not enough. Each script previews first, then asks for a reason before applying.
          Date range comes from the <strong>Scan</strong> tab ({fromDate && toDate ? `${fromDate} to ${toDate}` : 'set dates on Scan tab first'}).
        </p>
      </div>

      {activeFindingKey === STANDALONE_WIZARD_KEY && (
        <div className="admin-panel mb-3">
          {renderFixWizard?.()}
        </div>
      )}

      <AccountingRepairScriptsPanel
        pumpId={pumpId}
        fromDate={fromDate}
        toDate={toDate}
        scripts={repairScripts}
        intro={repairScriptsIntro}
        disabled={disabled}
        onPreviewStart={onPreviewStart}
        onPreviewEnd={onPreviewEnd}
        onApplyStart={onApplyStart}
        onApplyEnd={onApplyEnd}
        onCompleted={onCompleted}
      />

      <GasCylinderOwnersFundPanel
        pumpId={pumpId}
        fromDate={fromDate}
        toDate={toDate}
        disabled={disabled}
        onPreviewStart={onPreviewStart}
        onPreviewEnd={onPreviewEnd}
        onApplyStart={onApplyStart}
        onApplyEnd={onApplyEnd}
        onCompleted={onCompleted}
        onOpenHistory={onOpenHistory}
      />

      <FleetCardOwnersFundPanel
        pumpId={pumpId}
        fromDate={fromDate}
        toDate={toDate}
        disabled={disabled}
        onPreviewStart={onPreviewStart}
        onPreviewEnd={onPreviewEnd}
        onApplyStart={onApplyStart}
        onApplyEnd={onApplyEnd}
        onCompleted={onCompleted}
        onOpenHistory={onOpenHistory}
      />

      {fixesIntro && (
        <div className="admin-panel accounting-intro-box mb-3 mt-3">
          <h5 className="mb-1">{fixesIntro.title}</h5>
          <p className="small mb-0">{fixesIntro.body}</p>
        </div>
      )}

      {Object.entries(fixesByCategory).map(([cat, items]) => (
        <div className="admin-panel" key={cat}>
          <h5>{items[0]?.category_label || CATEGORY_LABELS[cat] || cat.replace(/_/g, ' ')}</h5>
          <div className="row g-2">
            {items.map((fix) => (
              <div className="col-md-6 col-lg-4" key={fix.fix_type}>
                <div className="accounting-tool-card">
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <strong>{fix.plain_label || fix.label}</strong>
                    {fix.risk && (
                      <span className={`admin-badge audit-severity ${RISK_CLASS[fix.risk] || 'info'}`}>
                        {RISK_LABELS[fix.risk] || fix.risk}
                      </span>
                    )}
                  </div>
                  <p className="small text-muted mb-1">{fix.plain_description || fix.description}</p>
                  {fix.when_to_use && <p className="small mb-2"><em>When:</em> {fix.when_to_use}</p>}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => onOpenFixWizard(fix)}
                  >
                    Open fix wizard
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="admin-panel">
        <h5>Past cost recalculations (WAC)</h5>
        <p className="small text-muted">
          If a &quot;Recalculate purchase cost&quot; fix made things worse, use Undo on the row below.
        </p>
        {wacAudits.length === 0 ? (
          <p className="small text-muted mb-0">No cost recalculation records for this pump.</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm admin-pump-table">
              <thead>
                <tr><th>ID</th><th>What was done</th><th>From date</th><th>Rows changed</th><th>By</th><th /></tr>
              </thead>
              <tbody>
                {wacAudits.map((a) => (
                  <tr key={a.id}>
                    <td>{a.id}</td>
                    <td>{a.action}</td>
                    <td>{a.from_date}</td>
                    <td>{a.rows_applied || a.rows_changed}</td>
                    <td className="small">{a.performed_by_name}</td>
                    <td>
                      {['apply', 'apply_sales', 'apply_purchase'].includes(a.action) && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => onOpenFixWizard(
                            { fix_type: 'wac_rollback', plain_label: 'Undo: Reverse last cost recalculation', params: {} },
                            { baseline_audit_id: a.id }
                          )}
                        >
                          Undo
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
